import React from 'react';
import {
  INamedEntityRecognitionAttribute,
  INamedEntityRecognitionRelationshipDefinition,
} from '@scale/shared/types/params/namedentityrecognition';
import { INamedEntityRecognitionResponseAttribute } from '@scale/shared/types/response/namedentityrecognition';
import {
  INamedEntityRecognitionLabel,
  getLabelColorPalette,
  IColorMap,
} from './namedEntityRecognitionLabels';
import NERAnnotationPopover from './NERAnnotationPopover';
import Fuse from 'fuse.js';
import { makeStyles } from 'design-system/styles';
import _ from 'lodash';
import { INamedEntityRecognitionRelationship } from './namedEntityRecognitionAnnotations';

export enum NERAnnotationFocusLevel {
  Disabled = 'disabled',
  Inactive = 'inactive',
  Hoverred = 'hoverred',
  Selected = 'selected',
  Faded = 'faded',
}

interface INERAnnotationProps {
  annotationId: string;
  clientBoxes: {
    top: number;
    left: number;
    height: number;
    width: number;
  }[];
  label: INamedEntityRecognitionLabel;
  labels: INamedEntityRecognitionLabel[];
  labelSearchIndex?: Fuse<INamedEntityRecognitionLabel>;
  relationships: INamedEntityRecognitionRelationship[];
  defaultAttributeFields?: Record<string, INamedEntityRecognitionAttribute>;
  relationshipDefinitions: INamedEntityRecognitionRelationshipDefinition[];
  labelAttributes?: { [key: string]: INamedEntityRecognitionAttribute };
  attributeValues?: { [key: string]: INamedEntityRecognitionResponseAttribute };
  showPopover: boolean;
  focusLevel: NERAnnotationFocusLevel;
  labelColormap: IColorMap;
  blockSelection: boolean;
  displayDepth: number;
  isAnnotationClumped: boolean;

  hiddenRelationships: { [id: string]: boolean };
  toggleRelationshipVisibility: (id: string) => void;

  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onClick?: (e: React.MouseEvent) => void;
  onChangeLabel?: (label: INamedEntityRecognitionLabel) => void;
  onChangeAttributeValue?: (key: string, value: INamedEntityRecognitionResponseAttribute) => void;
  onClickAway: () => void;
  onEnterLinkingMode: () => void;
  onRemoveRelationship: (relationshipId: string) => void;
  onSetAnnotationAnchorOffset: (offset: number) => void;
  removeFocusedAnnotation: () => void;
  checkRequiredAttributeExist: (key: string) => boolean;
}

const NERAnnotation: React.ForwardRefRenderFunction<HTMLElement, INERAnnotationProps> = (
  {
    annotationId,
    clientBoxes,
    label,
    labels,
    labelSearchIndex,
    relationships,
    defaultAttributeFields,
    hiddenRelationships,
    toggleRelationshipVisibility,
    relationshipDefinitions,
    labelAttributes,
    attributeValues,
    labelColormap,
    showPopover,
    focusLevel,
    blockSelection,
    displayDepth,
    isAnnotationClumped,
    onMouseEnter,
    onMouseLeave,
    onClick,
    onChangeLabel = () => {},
    onChangeAttributeValue = () => {},
    onClickAway,
    onEnterLinkingMode,
    onRemoveRelationship,
    onSetAnnotationAnchorOffset,
    removeFocusedAnnotation,
    checkRequiredAttributeExist,
  },
  containerRef,
) => {
  const labelColorPalette = getLabelColorPalette(label.name, labelColormap);
  let labelColor = labelColorPalette.labelUnderline;
  switch (focusLevel) {
    case NERAnnotationFocusLevel.Selected:
      labelColor = labelColorPalette.labelUnderlineFocused;
      break;
    case NERAnnotationFocusLevel.Hoverred:
      labelColor = labelColorPalette.labelUnderlineHoverred;
      break;
    case NERAnnotationFocusLevel.Disabled:
      labelColor = labelColorPalette.labelUnderlineDisabled;
      break;
  }

  const backgroundColor = labelColorPalette.annotationBackgroundActive;

  const computeAnchorOffsetFromElement = (el: HTMLDivElement | null) => {
    el && onSetAnnotationAnchorOffset(el.getBoundingClientRect().width / 2);
  };

  const classes = useNERAnnotationStyles({
    labelColor,
    backgroundColor,
    disabled: focusLevel === NERAnnotationFocusLevel.Disabled,
    hoverred: focusLevel === NERAnnotationFocusLevel.Hoverred,
    faded: focusLevel === NERAnnotationFocusLevel.Faded,
  });

  // TODO (CHAT-617): We should handle NER steps with empty provided text (and invalid annotations) more first-class
  if (!clientBoxes.length) {
    return null;
  }

  return (
    <>
      {showPopover && labelSearchIndex && (
        <NERAnnotationPopover
          annotationId={annotationId}
          ref={containerRef}
          horizontalOffset={-clientBoxes[0].left}
          verticalOffset={-_.last(clientBoxes)!.top - 32 - 3 * displayDepth}
          label={label}
          labels={labels}
          labelColormap={labelColormap}
          labelSearchIndex={labelSearchIndex}
          relationships={relationships}
          defaultAttributeFields={defaultAttributeFields}
          relationshipDefinitions={relationshipDefinitions}
          labelAttributes={labelAttributes}
          attributeValues={attributeValues}
          onChangeLabel={onChangeLabel}
          onChangeAttributeValue={onChangeAttributeValue}
          onClose={onClickAway}
          onEnterLinkingMode={onEnterLinkingMode}
          onRemoveRelationship={onRemoveRelationship}
          hiddenRelationships={hiddenRelationships}
          toggleRelationshipVisibility={toggleRelationshipVisibility}
          removeFocusedAnnotation={removeFocusedAnnotation}
          checkRequiredAttributeExist={checkRequiredAttributeExist}
        />
      )}
      {!isAnnotationClumped && (
        <div
          className={classes.annotationLabel}
          ref={computeAnchorOffsetFromElement}
          onClick={onClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          style={{
            top:
              displayDepth === 0
                ? clientBoxes[0].top - 11
                : clientBoxes[0].top + clientBoxes[0].height + 7,
            left: clientBoxes[0].left,
            maxWidth: clientBoxes[0].width,
          }}
        >
          {label.display_name ?? label.name}
        </div>
      )}
      {clientBoxes.map(({ top, left, height, width }, idx) => (
        <div key={idx}>
          <div
            ref={idx === 0 && isAnnotationClumped ? computeAnchorOffsetFromElement : null}
            className={classes.annotationLine}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            style={{
              top: top + height + 3 * displayDepth,
              left,
              height: 2,
              width,
            }}
          />
          {blockSelection && (
            <div
              className={classes.annotationSelectionBlocker}
              onClick={onClick}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              style={{ top, left, height, width }}
            />
          )}
          {[NERAnnotationFocusLevel.Selected, NERAnnotationFocusLevel.Hoverred].includes(
            focusLevel,
          ) && (
            <div className={classes.annotationBackground} style={{ top, left, height, width }} />
          )}
        </div>
      ))}
    </>
  );
};

const useNERAnnotationStyles = makeStyles<{
  labelColor: string;
  backgroundColor: string;
  disabled: boolean;
  hoverred: boolean;
  faded: boolean;
}>({
  annotationLabel: ({
    labelColor,
    disabled,
    hoverred,
    faded,
  }: {
    labelColor: string;
    disabled: boolean;
    hoverred: boolean;
    faded: boolean;
  }) => ({
    position: 'absolute',
    color: labelColor,
    backgroundColor: '#fff',
    overflow: hoverred ? 'visible' : 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    fontSize: 11,
    fontWeight: 500,
    lineHeight: '11px',
    zIndex: hoverred ? 3 : 2,
    cursor: disabled ? 'not-allowed' : 'pointer',
    userSelect: 'none',
    opacity: faded ? 0.3 : 1,
  }),
  annotationSelectionBlocker: ({ disabled }: { disabled: boolean }) => ({
    position: 'absolute',
    zIndex: 2,
    cursor: disabled ? 'not-allowed' : 'pointer',
    userSelect: 'none',
  }),
  annotationLine: ({
    labelColor,
    disabled,
    faded,
  }: {
    labelColor: string;
    disabled: boolean;
    faded: boolean;
  }) => ({
    position: 'absolute',
    backgroundColor: labelColor,
    zIndex: 2,
    cursor: disabled ? 'not-allowed' : 'pointer',
    userSelect: 'none',
    opacity: faded ? 0.3 : 1,
  }),
  annotationBackground: ({ backgroundColor }: { backgroundColor: string }) => ({
    position: 'absolute',
    backgroundColor,
    userSelect: 'none',
  }),
});

export default React.forwardRef(NERAnnotation);
